import { useRef } from "react";

/**
 * A reusable hook for caching API call results.
 *
 * This hook wraps an API function (which returns a Promise) and caches its results
 * using the arguments as the cache key. The TTL (time-to-live) is configurable; the default is 5 minutes.
 *
 * @param {function} apiFn - The API function to wrap. It should return a Promise.
 * @param {number} ttl - Cache time-to-live in milliseconds (default 300000 ms = 5 minutes).
 * @returns {function} A cached version of the API function.
 */
export function useApiCache(apiFn, ttl = 300000) {
    const cacheRef = useRef(new Map());

    const cachedApiFn = async (...args) => {
        const key = JSON.stringify(args);
        const now = Date.now();
        if (cacheRef.current.has(key)) {
            const cached = cacheRef.current.get(key);
            if (now < cached.expiry) {
                // Return cached response if it hasn't expired.
                return cached.response;
            }
            // Remove expired cache entry.
            cacheRef.current.delete(key);
        }
        // Execute the API function if no valid cache exists.
        const response = await apiFn(...args);
        // Cache the response with an expiry timestamp.
        cacheRef.current.set(key, { response, expiry: now + ttl });
        return response;
    };

    return cachedApiFn;
}